import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { BoldIcon, ItalicIcon, UnderlineIcon, HeadingIcon, NumberListIcon, BulletListIcon, LinkIcon } from '../icons/EditorIcons';
import { IconButton, Menu, MenuItem, Box } from '@mui/material';
const colorPalette = [
    '#000000',
    '#434343',
    '#666666',
    '#999999',
    '#B7B7B7',
    '#CCCCCC',
    '#D9D9D9',
    '#EFEFEF',
    '#FFFFFF',
    '#980000',
    '#FF0000',
    '#FF9900',
    '#FFFF00',
    '#00FF00',
    '#00FFFF',
    '#4A86E8',
    '#0000FF',
    '#9900FF',
    '#FF00FF',
    '#E6B8AF',
    '#F4CCCC',
    '#FCE5CD',
    '#FFF2CC',
    '#D9EAD3',
    '#D0E0E3',
    '#C9DAF8',
    '#CFE2F3',
    '#D9D2E9',
    '#EAD1DC',
    '#EA9999',
    '#F6B26B',
    '#FFE599',
    '#B6D7A8',
    '#A2C4C9',
    '#A4C2F4',
    '#B4A7D6',
    '#D5A6BD',
    '#CC0000',
    '#E69138',
    '#F1C232',
    '#6AA84F',
    '#45818E',
    '#3C78D8',
    '#674EA7',
    '#A64D79',
    '#d4176f',
    '#f91c83',
    '#ec0e6e'
];
const fontSizes = [
    { value: '10pt', label: '10pt' },
    { value: '12pt', label: '12pt' },
    { value: '14pt', label: '14pt' },
    { value: '16pt', label: '16pt' },
    { value: '18pt', label: '18pt' },
    { value: '20pt', label: '20pt' },
    { value: '24pt', label: '24pt' },
    { value: '36pt', label: '36pt' }
];
export const Toolbar = ({ editor }) => {
    const [headingAnchorEl, setHeadingAnchorEl] = useState(null);
    const [colorAnchorEl, setColorAnchorEl] = useState(null);
    const [highlightAnchorEl, setHighlightAnchorEl] = useState(null);
    const [sizeAnchorEl, setSizeAnchorEl] = useState(null);
    const currentColor = editor.getAttributes('textStyle')?.color || '#000000';
    const currentHighlight = editor.getAttributes('highlight')?.color || 'transparent';
    const currentSize = editor.getAttributes('textStyle')?.fontSize || '14pt';
    const handleHeadingClick = (event) => {
        setHeadingAnchorEl(event.currentTarget);
    };
    const handleColorClick = (event) => {
        setColorAnchorEl(event.currentTarget);
    };
    const handleHighlightClick = (event) => {
        setHighlightAnchorEl(event.currentTarget);
    };
    const handleSizeClick = (event) => {
        setSizeAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setHeadingAnchorEl(null);
        setColorAnchorEl(null);
        setHighlightAnchorEl(null);
        setSizeAnchorEl(null);
    };
    const applyHeading = (level) => {
        if (level) {
            editor
                .chain()
                .focus()
                .toggleHeading({ level: level })
                .run();
        }
        else {
            editor.chain().focus().setParagraph().run();
        }
        handleClose();
    };
    const applyColor = (color) => {
        editor.chain().focus().setColor(color).run();
        handleClose();
    };
    const applyHighlight = (color) => {
        if (color === 'transparent') {
            editor.chain().focus().unsetHighlight().run();
        }
        else {
            editor.chain().focus().toggleHighlight({ color }).run();
        }
        handleClose();
    };
    const applySize = (size) => {
        editor.chain().focus().setFontSize(size).run();
        handleClose();
    };
    return (_jsxs("div", { className: 'tiptap-toolbar', children: [_jsxs("div", { className: 'toolbar-group', children: [_jsx(IconButton, { size: 'small', onClick: () => editor.chain().focus().toggleBold().run(), className: editor.isActive('bold') ? 'active' : '', children: _jsx(BoldIcon, { size: 18 }) }), _jsx(IconButton, { size: 'small', onClick: () => editor.chain().focus().toggleItalic().run(), className: editor.isActive('italic') ? 'active' : '', children: _jsx(ItalicIcon, { size: 18 }) }), _jsx(IconButton, { size: 'small', onClick: () => editor.chain().focus().toggleUnderline().run(), className: editor.isActive('underline') ? 'active' : '', children: _jsx(UnderlineIcon, { size: 18 }) })] }), _jsx("div", { className: 'toolbar-divider' }), _jsxs("div", { className: 'toolbar-group', children: [_jsx(IconButton, { size: 'small', onClick: handleSizeClick, children: _jsx("span", { style: { fontSize: '12px' }, children: currentSize }) }), _jsx(Menu, { anchorEl: sizeAnchorEl, open: Boolean(sizeAnchorEl), onClose: handleClose, children: fontSizes.map((size) => (_jsx(MenuItem, { onClick: () => applySize(size.value), selected: currentSize === size.value, children: size.label }, size.value))) })] }), _jsx("div", { className: 'toolbar-divider' }), _jsxs("div", { className: 'toolbar-group', children: [_jsx(IconButton, { size: 'small', onClick: handleColorClick, children: _jsx(Box, { sx: {
                                width: 20,
                                height: 20,
                                borderRadius: '4px',
                                backgroundColor: currentColor,
                                border: '1px solid #ccc'
                            } }) }), _jsx(Menu, { anchorEl: colorAnchorEl, open: Boolean(colorAnchorEl), onClose: handleClose, children: _jsx(Box, { sx: { p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 300 }, children: colorPalette.map((color) => (_jsx(Box, { onClick: () => applyColor(color), sx: {
                                    width: 24,
                                    height: 24,
                                    borderRadius: '4px',
                                    backgroundColor: color,
                                    border: currentColor === color ? '2px solid #1976d2' : '1px solid #ccc',
                                    cursor: 'pointer',
                                    '&:hover': { border: '2px solid #1976d2' }
                                } }, color))) }) }), _jsx(IconButton, { size: 'small', onClick: handleHighlightClick, children: _jsx(Box, { sx: {
                                width: 20,
                                height: 20,
                                borderRadius: '4px',
                                backgroundColor: currentHighlight === 'transparent' ? '#fff' : currentHighlight,
                                border: '2px solid #ccc'
                            } }) }), _jsx(Menu, { anchorEl: highlightAnchorEl, open: Boolean(highlightAnchorEl), onClose: handleClose, children: _jsxs(Box, { sx: { p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 300 }, children: [_jsx(Box, { onClick: () => applyHighlight('transparent'), sx: {
                                        width: 24,
                                        height: 24,
                                        borderRadius: '4px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #ccc',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '&:hover': { border: '2px solid #1976d2' }
                                    }, children: _jsx("span", { style: { fontSize: '10px' }, children: "\u00D7" }) }), colorPalette.map((color) => (_jsx(Box, { onClick: () => applyHighlight(color), sx: {
                                        width: 24,
                                        height: 24,
                                        borderRadius: '4px',
                                        backgroundColor: color,
                                        border: currentHighlight === color ? '2px solid #1976d2' : '1px solid #ccc',
                                        cursor: 'pointer',
                                        '&:hover': { border: '2px solid #1976d2' }
                                    } }, color)))] }) })] }), _jsx("div", { className: 'toolbar-divider' }), _jsxs("div", { className: 'toolbar-group', children: [_jsx(IconButton, { size: 'small', onClick: handleHeadingClick, children: _jsx(HeadingIcon, { size: 18 }) }), _jsxs(Menu, { anchorEl: headingAnchorEl, open: Boolean(headingAnchorEl), onClose: handleClose, children: [_jsx(MenuItem, { onClick: () => applyHeading(1), children: "H1" }), _jsx(MenuItem, { onClick: () => applyHeading(2), children: "H2" }), _jsx(MenuItem, { onClick: () => applyHeading(3), children: "H3" }), _jsx(MenuItem, { onClick: () => applyHeading(null), children: "Normal" })] })] }), _jsx("div", { className: 'toolbar-divider' }), _jsxs("div", { className: 'toolbar-group', children: [_jsx(IconButton, { size: 'small', onClick: () => editor.chain().focus().toggleBulletList().run(), className: editor.isActive('bulletList') ? 'active' : '', children: _jsx(BulletListIcon, { size: 18 }) }), _jsx(IconButton, { size: 'small', onClick: () => editor.chain().focus().toggleOrderedList().run(), className: editor.isActive('orderedList') ? 'active' : '', children: _jsx(NumberListIcon, { size: 18 }) })] }), _jsx("div", { className: 'toolbar-divider' }), _jsx("div", { className: 'toolbar-group', children: _jsx(IconButton, { size: 'small', onClick: () => {
                        const url = window.prompt('Enter URL:');
                        if (url) {
                            editor.chain().focus().setLink({ href: url }).run();
                        }
                    }, className: editor.isActive('link') ? 'active' : '', children: _jsx(LinkIcon, { size: 18 }) }) })] }));
};
